<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.programmings', 2),
                        href: meta.links.index,
                    },
                    {
                        name: programming.title,
                        href: meta.links.show,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-6 md:gap-6">
                    <div class="md:col-span-6">
                        <label for="title" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.title') }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="text"
                                id="title"
                                name="title"
                                v-model="form.title"
                                class="w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                required
                                minlength="5"
                            />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.trimester') }}
                        </label>
                        <div class="mt-1">
                            {{ programming.trimester }}
                        </div>
                        <div class="mt-1 rounded-md shadow-sm hidden">
                            <select
                                id="trimester"
                                name="trimester"
                                v-model="form.trimester"
                                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value="" disabled>
                                    {{ $t('messages.select') }}
                                </option>
                                <option :value="1">1</option>
                                <option :value="2">2</option>
                                <option :value="3">3</option>
                            </select>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.grade_level') }}
                        </label>
                        <div class="mt-1">
                            {{ programming.grade_level_names }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.groups') }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm" v-if="groups.length">
                            <label class="ml-1 mr-3" v-for="group in groups" :key="group.id">
                                <input
                                    name="groups[]"
                                    type="checkbox"
                                    :value="group.id"
                                    v-model="form.group_ids"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                    {{ group.name }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.subject', 2) }}
                        </label>
                        <div class="mt-1 rounded-md shadow-sm">
                            <div class="flex flex-wrap space-x-2">
                                <BadgePill v-for="subject in selectedSubjects" :key="subject.id">
                                    {{ subject.name }}
                                </BadgePill>
                            </div>

                            <AppButton
                                element="button"
                                type="button"
                                class="mt-1"
                                @click="showSubjectsSlideOver = !showSubjectsSlideOver"
                            >
                                {{ $t('messages.edit') }}
                            </AppButton>
                            <FormError :value="form.errors.subject_ids" class="mt-2" />
                        </div>
                        <SlideOver :show="showSubjectsSlideOver" @close="showSubjectsSlideOver = false">
                            <ul>
                                <li v-for="subject in subjects" :key="subject.id">
                                    <label class="ml-1 mr-3">
                                        <input
                                            name="subjects[]"
                                            type="checkbox"
                                            :value="subject.id"
                                            v-model="form.subject_ids"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                            {{ subject.name }}
                                        </span>
                                    </label>
                                </li>
                            </ul>
                        </SlideOver>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.short_description') }}
                </template>
                <FormLabel :value="$t('messages.short_description')" for="short_description" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="short_description"
                    v-model="form.short_description"
                    name="short_description"
                    type="text"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.short_description" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.methodology') }}
                </template>
                <FormLabel :value="$t('messages.methodology')" for="methodology" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="methodology"
                    v-model="form.methodology"
                    name="methodology"
                    type="text"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.methodology" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.diversity_observations') }}
                </template>
                <FormLabel
                    :value="$t('messages.diversity_observations')"
                    for="diversity_observations"
                    class="sr-only"
                />
                <FormTextarea
                    rows="3"
                    id="diversity_observations"
                    v-model="form.diversity_observations"
                    name="diversity_observations"
                    type="text"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.diversity_observations" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.learning_aims') }}
                </template>
                <FormLabel :value="$t('messages.learning_aims')" for="learning_aims" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="learning_aims"
                    v-model="form.learning_aims"
                    name="learning_aims"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.learning_aims" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.general_contents') }}
                </template>
                <FormLabel :value="$t('messages.general_contents')" for="general_contents" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="general_contents"
                    v-model="form.general_contents"
                    name="general_contents"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.general_contents" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection class="hidden">
                <template v-slot:title>
                    {{ $t('messages.evaluation_criteria') }}
                </template>
                <FormLabel :value="$t('messages.evaluation_criteria')" for="evaluation_criteria" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="evaluation_criteria"
                    v-model="form.evaluation_criteria"
                    name="evaluation_criteria"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.evaluation_criteria" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.general_competences') }}
                </template>
                <FormLabel :value="$t('messages.general_competences')" for="general_competences" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="general_competences"
                    v-model="form.general_competences"
                    name="general_competences"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.general_competences" class="mt-2" />
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.learning_vectors') }}
                </template>
                <FormLabel :value="$t('messages.learning_vectors')" for="learning_vectors" class="sr-only" />
                <FormTextarea
                    rows="3"
                    id="learning_vectors"
                    v-model="form.learning_vectors"
                    name="learning_vectors"
                    class="mt-1 w-full"
                />
                <FormError :value="form.errors.learning_vectors" class="mt-2" />
            </TitleFieldsFormSection>

            <AlertMessage class="mt-5 mb-3" :class="subjectsToBeDeleted.length > 0 ? 'visible' : 'invisible'">
                <template v-slot:title>
                    {{ $t('messages.warning') }}. {{ $t('messages.programming_session_deletion_warning') }}
                </template>
                <template v-slot:body>
                    <span v-for="subject in subjectsToBeDeleted" :key="subject.id" class="mr-2">
                        {{ subject.name }}
                    </span>
                </template>
            </AlertMessage>

            <div>
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import BadgePill from '@/components/BadgePill.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AppButton from '@/components/AppButton.vue';
import SlideOver from '@/components/SlideOver.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import FormError from '@/components/Forms/FormError.vue';

export default {
    components: {
        FormError,
        FormTextarea,
        FormLabel,
        SlideOver,
        AppButton,
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        BadgePill,
        AlertMessage,
    },
    props: ['meta', 'defaults', 'gradeLevels', 'subjects', 'programming'],
    layout: Layout,
    data() {
        return {
            form: this.$inertia.form({
                title: this.programming.title,
                grade_level_ids: this.programming.grade_level_ids,
                group_ids: this.programming.group_ids,
                subject_ids: this.programming.subject_ids,
                trimester: this.programming.trimester,
                methodology: this.programming.methodology,
                diversity_observations: this.programming.diversity_observations,
                short_description: this.programming.short_description,
                general_contents: this.programming.general_contents,
                evaluation_criteria: this.programming.evaluation_criteria,
                learning_aims: this.programming.learning_aims,
                general_competences: this.programming.general_competences,
                learning_vectors: this.programming.learning_vectors,
            }),
            showSubjectsSlideOver: false,
        };
    },
    name: 'Edit',
    computed: {
        groups: function () {
            return this.form.grade_level_ids
                ? this.gradeLevels.filter((gl) => this.form.grade_level_ids.includes(gl.id)).flatMap((gl) => gl.groups)
                : [];
        },
        selectedSubjects: function () {
            return this.subjects.filter((s) => this.form.subject_ids.includes(s.id));
        },
        subjectsToBeDeleted: function () {
            return this.subjects
                .filter((s) => this.programming.subject_ids.includes(s.id))
                .filter((s) => !this.form.subject_ids.includes(s.id));
        },
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
    },
};
</script>
